import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  Input,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, NgClass, isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface TocItem      { id: string; label: string; }
export interface ProcessCard  { icon: string; title: string; description: string; }
export interface CompareRow   { term: string; termFull: string; tagClass: string; manages: string; focus: string; }
export interface BenefitItem  { num: string; title: string; description: string; }
export interface FaqItem      { question: string; answer: string; open: boolean; }

export interface DefinitionSection {
  id: string;
  eyebrow: string;
  title: string;
  definitionHtml: string;
  definitionCite: string;
  bodyHtml: string[];
}

export interface ProcessesSection {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  items: ProcessCard[];
}

export interface CompareSection {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  rows: CompareRow[];
  headers?: [string, string, string];
}

export interface BenefitsSection {
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  items: BenefitItem[];
}

export interface FaqSection {
  id: string;
  eyebrow: string;
  title: string;
  items: FaqItem[];
}

export interface PillarConfig {
  tocItems: TocItem[];
  definition: DefinitionSection;
  /** Optional - omit to hide the processes section on a page */
  processes?: ProcessesSection;
  compare: CompareSection;
  benefits: BenefitsSection;
  faq: FaqSection;
}

@Component({
  selector: 'app-content-layout',
  standalone: true,
  imports: [CommonModule, NgClass],
  templateUrl: './pillar-layout.html',
  styleUrls: ['./pillar-layout.scss'],
})
export class PillarLayout implements OnInit, AfterViewInit, OnDestroy {
  @Input() config!: PillarConfig;
  @Input() accentColor = '#FF932C';

  constructor(
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  activeSection = '';
  isTocSticky = false;

  private observer!: IntersectionObserver;
  private layoutObserver!: IntersectionObserver;

  private readonly NAV_HEIGHT = 64;
  private readonly SCROLL_BUFFER = 24;
  private get TOTAL_OFFSET(): number {
    return this.NAV_HEIGHT + this.SCROLL_BUFFER;
  }

  safe(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** The compare table's third column is hidden when no row provides a focus value */
  get compareHasFocus(): boolean {
    return !!this.config?.compare?.rows?.some(row => !!row.focus?.trim());
  }

  // ─── LIFECYCLE ────────────────────────────────────────

  ngOnInit(): void {
    this.activeSection = this.config?.tocItems?.[0]?.id ?? '';
    if (isPlatformBrowser(this.platformId)) {
      this.initSectionScrollSpy();
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.observeSections();
    this.initLayoutObserver();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.layoutObserver?.disconnect();
  }

  // ─── LAYOUT OBSERVER ──────────────────────────────────

  private initLayoutObserver(): void {
    const layoutEl = this.el.nativeElement.querySelector('.content-layout');
    if (!layoutEl) return;

    this.layoutObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const rect = entry.boundingClientRect;
          const viewportMiddle = window.innerHeight / 2;

          if (entry.isIntersecting) {
            this.isTocSticky = rect.top <= viewportMiddle;
          } else {
            this.isTocSticky = false;
          }
          this.cdr.detectChanges();
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    );

    this.layoutObserver.observe(layoutEl);
  }

  // ─── SECTION SCROLL SPY ───────────────────────────────

  private initSectionScrollSpy(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id;
            this.cdr.detectChanges();
          }
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    );
  }

  private observeSections(): void {
    this.config?.tocItems?.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) this.observer.observe(el);
    });
  }

  // ─── TOC CLICK SCROLL ─────────────────────────────────

  scrollTo(id: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const el = document.getElementById(id);
    if (!el) return;

    const top =
      el.getBoundingClientRect().top +
      window.scrollY -
      (this.TOTAL_OFFSET + 150);

    window.scrollTo({ top, behavior: 'smooth' });
  }

  // ─── FAQ ACCORDION ────────────────────────────────────

  toggleFaq(index: number): void {
    this.config.faq.items = this.config.faq.items.map((faq, i) => ({
      ...faq,
      open: i === index ? !faq.open : false,
    }));
  }
}
